import type {
  OrgLlmConnectionKind,
  OrgLlmConnectionRecord,
  OrgLlmConnectionTemplate,
  OrgLlmProviderTemplatesResponse,
  UpsertOrgLlmConnectionRequest,
} from "@/shared/domain/orgs";

/** A connection as the add / edit dialog edits it: empty strings for unset fields. */
export interface ConnectionDraft {
  kind: OrgLlmConnectionKind;
  label: string;
  base_url: string;
  api_key_secret_name: string;
  /** The provider model id the server tests the connection with before storing it. */
  test_model_id: string;
}

/** Whether the dialog adds a connection or edits one; only adding requires a test model. */
export type ConnectionDialogMode = "add" | "edit";

/** Per-kind defaults the form pre-fills; a kind without one starts blank. */
export type ConnectionTemplates =
  OrgLlmProviderTemplatesResponse["connections"];

/** The draft for editing `connection`, or a new Anthropic API connection. */
export function initialConnectionDraft(
  connection: OrgLlmConnectionRecord | undefined,
  templates: ConnectionTemplates,
): ConnectionDraft {
  if (connection !== undefined) {
    return {
      kind: connection.settings.kind,
      label: connection.label,
      base_url: connection.settings.base_url ?? "",
      api_key_secret_name: connection.settings.api_key_secret_name ?? "",
      test_model_id: "",
    };
  }
  return withConnectionKind(
    {
      kind: "anthropic_api",
      label: "",
      base_url: "",
      api_key_secret_name: "",
      test_model_id: "",
    },
    "anthropic_api",
    templates,
  );
}

/**
 * The draft switched to `kind`. An OpenAI-compatible connection's base URL is
 * pre-filled from its template; an Anthropic API connection's gateway URL
 * starts empty, since without one the server calls the Anthropic API directly.
 * The test model is pre-filled from the template too (see {@link templateTestModelId}).
 */
export function withConnectionKind(
  draft: ConnectionDraft,
  kind: OrgLlmConnectionKind,
  templates: ConnectionTemplates,
): ConnectionDraft {
  const baseUrl =
    kind === "anthropic_api" ? "" : (templates[kind]?.base_url ?? "");
  return {
    ...draft,
    kind,
    base_url: baseUrl,
    test_model_id: templateTestModelId(templates[kind]),
  };
}

/**
 * The model to test a new connection of the template's kind with: its
 * utility-only suggestion (the cheapest call) when it has one, else its
 * first suggestion, else nothing (an OpenAI-compatible endpoint's models are
 * the org's own to name).
 */
export function templateTestModelId(
  template: OrgLlmConnectionTemplate | undefined,
): string {
  const models = template?.models ?? [];
  return (
    (models.find((m) => m.in_picker === false) ?? models[0])?.model_id ?? ""
  );
}

/**
 * Why the draft cannot be saved, or `null` when it can. The server also
 * checks the URL and the secret, and whether the test call succeeds.
 */
export function connectionDraftError(
  draft: ConnectionDraft,
  mode: ConnectionDialogMode,
): string | null {
  if (draft.label.trim() === "") {
    return "Give the connection a label.";
  }
  if (draft.kind === "openai_compat" && draft.base_url.trim() === "") {
    return "An OpenAI-compatible connection needs a base URL.";
  }
  if (
    draft.kind === "anthropic_api" &&
    draft.api_key_secret_name.trim() === ""
  ) {
    return "An Anthropic API connection needs an API key secret.";
  }
  if (mode === "add" && draft.test_model_id.trim() === "") {
    return "Name a model to test the connection with.";
  }
  return null;
}

/**
 * Whether saving the draft changes the connection's settings, which the
 * server verifies with a model call; a label-only edit makes none.
 */
export function connectionSettingsChanged(
  draft: ConnectionDraft,
  connection: OrgLlmConnectionRecord,
): boolean {
  const { settings } = connectionRequest(draft);
  const saved = connection.settings;
  return (
    settings.kind !== saved.kind ||
    (settings.base_url ?? null) !== (saved.base_url ?? null) ||
    (settings.api_key_secret_name ?? null) !==
      (saved.api_key_secret_name ?? null)
  );
}

/** The create / update body for a draft {@link connectionDraftError} accepts. A blank test model is sent as `null`. */
export function connectionRequest(
  draft: ConnectionDraft,
): UpsertOrgLlmConnectionRequest {
  const baseUrl = draft.base_url.trim();
  const secret = draft.api_key_secret_name.trim();
  const label = draft.label.trim();
  const testModelId = draft.test_model_id.trim();
  const test_model_id = testModelId === "" ? null : testModelId;
  if (draft.kind === "anthropic_api") {
    return {
      label,
      test_model_id,
      settings: {
        kind: "anthropic_api",
        base_url: baseUrl === "" ? null : baseUrl,
        api_key_secret_name: secret,
      },
    };
  }
  return {
    label,
    test_model_id,
    settings: {
      kind: "openai_compat",
      base_url: baseUrl,
      api_key_secret_name: secret === "" ? null : secret,
    },
  };
}
